import { useEffect, useMemo, useState } from 'react';
import { Channel, Event } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';
import { filterAvailableChannels } from '@features/components/Chat/utils/shareToChat.utils';
import { useStreamChatContext } from '@features/contexts/ChatProvider';
import { isNil } from 'lodash-es';
import { StreamChannelType } from '@/api/apis/chat';
import { StreamEventType } from '../../constants';

export const useChatChannelsState = (
  excludedChatTypes: StreamChannelType[],
  chatGrants: StreamChannelType[] | null,
) => {
  const { client } = useChatContext();
  const { streamChatFilters } = useStreamChatContext();
  const [channels, setChannels] = useState<Channel[]>([]);

  useEffect(() => {
    if (client && !isNil(streamChatFilters)) {
      client.queryChannels(streamChatFilters).then(setChannels);
    }
  }, [client, streamChatFilters]);

  useEffect(() => {
    const handleAddedToChannel = (event: Event) => {
      if (event.channel) {
        setChannels((prevChannels) => [...prevChannels, client.channel(event.channel!.type, event.channel!.id)]);
      }
    };

    const handleRemovedFromChannel = (event: Event) => {
      setChannels((prevChannels) => prevChannels.filter((channel) => channel.id !== event.channel?.id));
    };

    client?.on(StreamEventType.ADDED_TO_CHANNEL, handleAddedToChannel);
    client?.on(StreamEventType.REMOVED_FROM_CHANNEL, handleRemovedFromChannel);

    return () => {
      client?.off(StreamEventType.ADDED_TO_CHANNEL, handleAddedToChannel);
      client?.off(StreamEventType.REMOVED_FROM_CHANNEL, handleRemovedFromChannel);
    };
  }, [client]);

  return useMemo(
    () => filterAvailableChannels(channels, excludedChatTypes, chatGrants),
    [channels, excludedChatTypes, chatGrants],
  );
};
